import { Button } from "components/ui/button";
import { ContentContainer } from "@/components/layout/content-container";
import { PageLayout } from "@/components/layout/page-layout";
import { PageTitle } from "@/components/layout/page-title";
import { RefreshCw, FileSpreadsheet } from "lucide-react";
import React from "react";
import { FileUpload } from "./components/file-upload";
import {
  DuplicateConfirmModal,
  ConfirmActionModal,
} from "./components/duplicate-confirm-modal";
import { ImportHistoryTable } from "./components/import-history-table";
import { useDtefImportFlow } from "./hooks/use-dtef-import-flow";
import type { SameFileImport } from "@/@types/dtfe-import";

export const DtefImport: React.FC = () => {
  const {
    selectedFile,
    setSelectedFile,
    importType,
    setImportType,
    isLoading,
    handleUpload,
    executeUpload,
    handleClearAll,
    closeModal,
    handleModal,
  } = useDtefImportFlow();

  const showSecondConfirmation = (action: "replace" | "append") => {
    const actionLabel = action === "replace" ? "substituir" : "incluir";

    handleModal({
      isOpen: true,
      title: "Confirmar ação",
      element: (
        <ConfirmActionModal
          actionLabel={actionLabel}
          onConfirm={() => {
            closeModal();
            executeUpload(action);
          }}
          onCancel={closeModal}
        />
      ),
      onClose: closeModal,
    });
  };

  const showDuplicateModal = (
    existingCount: number,
    sameFileImports: SameFileImport[],
  ) => {
    handleModal({
      isOpen: true,
      title:
        sameFileImports.length > 0
          ? "Arquivo já importado"
          : "Registros duplicados encontrados",
      element: (
        <DuplicateConfirmModal
          existingCount={existingCount}
          sameFileImports={sameFileImports}
          onReplace={() => {
            closeModal();
            showSecondConfirmation("replace");
          }}
          onAppend={() => {
            closeModal();
            showSecondConfirmation("append");
          }}
          onCancel={closeModal}
        />
      ),
      onClose: closeModal,
    });
  };

  const onUpload = async () => {
    const result = await handleUpload();
    if (result?.duplicateDetected) {
      showDuplicateModal(result.existingCount ?? 0, result.sameFileImports ?? []);
    }
  };

  return (
    <PageLayout>
      <PageTitle
        title="Formas de Pagamento"
        description="Carregue arquivos contendo transações DTEF para importação no sistema"
      />
      <ContentContainer>
        <FileUpload
          selectedFile={selectedFile}
          onFileSelect={setSelectedFile}
          onUpload={onUpload}
          isLoading={isLoading}
          importType={importType}
          onImportTypeChange={setImportType}
        />

        <div className="flex flex-wrap gap-3 justify-between w-full mt-8">
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              onClick={handleClearAll}
              disabled={isLoading}
            >
              <RefreshCw className="mr-2 h-4 w-4" /> Limpar Tudo
            </Button>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              className="bg-[#F6D040] text-[#613F4F] hover:bg-[#F6D040]/90"
              onClick={onUpload}
              disabled={isLoading || !selectedFile}
            >
              {isLoading ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Importando...
                </>
              ) : (
                <>
                  <FileSpreadsheet className="mr-2 h-4 w-4" /> Importar D-TEF
                </>
              )}
            </Button>
          </div>
        </div>

        <ImportHistoryTable />
      </ContentContainer>
    </PageLayout>
  );
};
